import { useState } from "react";
import { BuzerIcon } from "../../../BuzerIcons";
import { DIFF_POINTS, DIFF_SK, DIFF_STARS, DIFFICULTIES, STREAK_COEF, normDifficulty, pointsFor } from "../../../lib/points";
import { Tag } from "./Tag";
import { Modal } from "../Modal";

// v29 §7.1 — štítok náročnosti návyku/výzvy: 1–3 plné hviezdičky + slovenský názov.
// Postavený na Tagu (tón "white" = papierový chip, čitateľný priamo na povrchu sheetu),
// takže nepribúda žiadny ad-hoc inline štítok (CLAUDE.md §6a pravidlo 2).
//   level     — "easy" | "normal" | "hard" (čokoľvek iné/NULL sa číta ako "normal")
//   size      — "xs" | undefined (xs = drobný variant do stiesnených miest)
//   showLabel — false ⇒ len hviezdičky (aria-label nesie plný text)
//   info      — pridá „i", ktoré otvorí vysvetlenie aktuálneho bodovania (§7.3).
//               `streak` sa doň premietne, nech človek vidí SVOJE číslo, nie len tabuľku.
// V Lite režime sa štítok NEZOBRAZUJE vôbec (§8.3) — o to sa stará volajúci.
export function DifficultyTag({ level, size, showLabel = true, info = false, streak = 0, className = "", ...rest }) {
  const [open, setOpen] = useState(false);
  const d = normDifficulty(level);
  const stars = DIFF_STARS[d];
  const glyph = size === "xs" ? 9 : 11;
  return (
    <>
      {/* f-batch #5 — vlastná trieda `bz-difftag`: štítok stojí v rade so Stat pilulkami (.bz-tile),
          ktoré sú vyššie, a musí im výškou sedieť. Rieši to primitives.css, nie inline štýl. */}
      <Tag tone="white" size={size} className={`bz-difftag${className ? " " + className : ""}`} aria-label={`Náročnosť: ${DIFF_SK[d]}`} {...rest}>
        <span className="bz-stars" aria-hidden="true">
          {Array.from({ length: stars }).map((_, i) => (
            <BuzerIcon key={i} name="star" size={glyph} color="currentColor" />
          ))}
        </span>
        {showLabel && DIFF_SK[d]}
        {info && (
          <button type="button" className="bz-tag__info" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            aria-label="Ako sa počítajú body" title="Ako sa počítajú body">
            <BuzerIcon name="info" size={glyph + 2} color="currentColor" />
          </button>
        )}
      </Tag>
      {open && <DifficultyInfo level={d} streak={streak} onClose={() => setOpen(false)} />}
    </>
  );
}

// Vysvetlenie bodovania — nahrádza pôvodnú vetu pod dlaždicami (§7.3). Ukazuje celú
// tabuľku a v nej zvýraznené TVOJE aktuálne pásmo, nech je zrejmé, prečo máš práve toľko.
export function DifficultyInfo({ level, streak = 0, onClose }) {
  const d = normDifficulty(level);
  const next = Math.max(1, (streak || 0) + 1);
  // pásmo, do ktorého spadne ĎALŠIE splnenie
  const bandIdx = STREAK_COEF.reduce((acc, [min], i) => (next >= min ? i : acc), 0);
  return (
    // Modal (nie Sheet): vnorený Sheet by na Escape zavrel aj detail pod sebou — oba
    // vešajú listener na window. Modal navyše rieši aj tlačidlo Späť (useModalBackClose).
    <Modal center maxWidth={400} title="Ako sa počítajú body" subtitle="Náročnosť × séria"
      iconNode={<BuzerIcon name="info" size={20} color="currentColor" />} onClose={onClose}>
      <div className="bz-diffinfo">
        <p className="bz-diffinfo__lead">Každý návyk má náročnosť. Za splnenie dostaneš základ, znásobený tým, ako dlhú sériu držíš.</p>

        <div className="bz-diffinfo__rows">
          {DIFFICULTIES.map((k) => (
            <div key={k} className={`bz-diffinfo__row${k === d ? " is-current" : ""}`}>
              <span className="bz-stars" aria-hidden="true">
                {Array.from({ length: DIFF_STARS[k] }).map((_, i) => <BuzerIcon key={i} name="star" size={10} color="currentColor" />)}
              </span>
              <span className="bz-diffinfo__name">{DIFF_SK[k]}</span>
              <span className="bz-diffinfo__val">{DIFF_POINTS[k]} b</span>
            </div>
          ))}
        </div>

        <div className="bz-diffinfo__rows">
          {STREAK_COEF.map(([min, coef], i) => {
            const to = STREAK_COEF[i + 1] ? STREAK_COEF[i + 1][0] - 1 : null;
            return (
              <div key={min} className={`bz-diffinfo__row${i === bandIdx ? " is-current" : ""}`}>
                <span className="bz-diffinfo__name">{to ? `${min}.–${to}. splnenie` : `${min}. a viac`}</span>
                <span className="bz-diffinfo__val">×{String(coef).replace(".", ",")}</span>
              </div>
            );
          })}
        </div>

        <p className="bz-diffinfo__now">
          Tento návyk je <strong>{DIFF_SK[d]}</strong>{(streak || 0) > 0 ? <> a máš sériu <strong>{streak}</strong></> : null} —
          za ďalšie splnenie dostaneš <strong>{pointsFor(d, next)} b</strong>.
          {pointsFor(d, next) < DIFF_POINTS[d] * 2 && <> Pri sérii 30 a viac to bude <strong>{DIFF_POINTS[d] * 2} b</strong>.</>}
        </p>
      </div>
    </Modal>
  );
}
